'use client'
import React from 'react';
import { useEffect, useState, useRef } from 'react'

import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react'
import clsx from 'clsx'

import { Container } from './Container'
import backgroundImage from '../../assets/images/background-features.jpg';

// Import video placeholders instead of images
// We maintain the same naming convention for easier replacement later
import complianceDashboardVideo from '../../assets/videos/features/COMPLIANCE_DASHBOARD.mov'
import processNarrativeVideo from '../../assets/videos/features/PROCESS_NARRATIVE.mov'
import userAccessCreationVideo from '../../assets/videos/features/USER_ACCESS_CREATION.mov'
import userAccessReview from '../../assets/videos/features/USER_ACCESS_REVIEW.mov'

import testProcedureGenerationVideo from '../../assets/videos/features/TEST_PROCEDURE_GENERATION.mov'
import rcmMappingVideo from '../../assets/videos/features/RISK_AND_CONTROL_MAPPING.mov'
import documentationVideo from '../../assets/videos/features/AUDIT_DOCUMENTATION.mov'
import sodAnalysisVideo from '../../assets/videos/features/SOD_ANALYSIS.mov'

import { getVideoThumbnail, generateThumbnailFromVideo } from '../../utils/videoUtils';

// Icons for feature types - replace with your own SVG icons if needed
const AdminIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 0 1-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0 1 15 18.257V17.25m6-12V15a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 15V5.25m18 0A2.25 2.25 0 0 0 18.75 3H5.25A2.25 2.25 0 0 0 3 5.25m18 0V12a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 12V5.25" />
  </svg>
)

const AuditIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z" />
  </svg>
)

const features = [
  {
    title: 'Automated User Access Review',
    description:
      "Streamline periodic access reviews with AI-powered automation that identifies high-risk access patterns, tracks reviewer actions, and maintains a complete audit trail—reducing review time by up to 80%.",
    video: userAccessReview,
    icon: <AdminIcon />,
  },
  {
    title: 'Real-Time SOX Compliance Dashboard',
    description:
      "Get instant visibility into your compliance status with live metrics, control effectiveness scoring, and automatic issue flagging. Know exactly where you stand at all times and where to focus your attention.",
    video: complianceDashboardVideo,
    icon: <AdminIcon />,
  },
  {
    title: 'AI Process Narrative Generator',
    description:
      "Transform system admin input into comprehensive process narratives that meet audit standards. The AI creates detailed documentation that identifies controls, risks, and mitigation strategies automatically.",
    video: processNarrativeVideo,
    icon: <AdminIcon />,
  },
  {
    title: 'Integrated Access Request Management',
    description:
      "Manage the entire access lifecycle from request to approval to provisioning with built-in SoD checking, approval workflows, and full audit trail. Eliminate manual handoffs and spreadsheet tracking.",
    video: userAccessCreationVideo,
    icon: <AdminIcon />,
  },
]

const auditFeatures = [
  {
    title: 'AI-Powered Audit Libraries',
    description:
      "Leverage advanced AI to automatically generate detailed audit procedures, identify key risks, and design effective controls tailored to your organization's needs. Our AI assistant helps create comprehensive audit documentation while ensuring compliance with professional standards.",
    video: testProcedureGenerationVideo,
    icon: <AuditIcon />,
  },
  {
    title: 'Automated Risk and Control Mapping',
    description:
      "Map and document relationships between risks and controls in a centralized platform. Create comprehensive risk matrices, track control coverage, and maintain clear visibility into your risk management framework with powerful visualization tools.",
    video: rcmMappingVideo,
    icon: <AuditIcon />,
  },
  {
    title: 'Audit Documentation Management',
    description:
      "Centralize all audit documentation with AI-powered organization, search, and retrieval. The system automatically links evidence to relevant controls and findings.",
    video: documentationVideo,
    icon: <AuditIcon />,
  },
  {
    title: 'SoD Ruleset Management',
    description:
      "Create, maintain and monitor Segregation of Duties rules across your systems. Automatically flag violations and track remediation efforts with comprehensive reporting.",
    video: sodAnalysisVideo,
    icon: <AuditIcon />,
  },
]


export function PrimaryFeatures() {
  let [tabOrientation, setTabOrientation] = useState('horizontal')
  let [selectedTab, setSelectedTab] = useState('sysadmin')
  let [selectedFeatureIndex, setSelectedFeatureIndex] = useState(0)
  let [isVideoLoaded, setIsVideoLoaded] = useState(false)
  let [isAutoRotating, setIsAutoRotating] = useState(true)
  let [touchStartX, setTouchStartX] = useState(null)
  let [swiping, setSwiping] = useState(false)
  let [completedFirstTabRotation, setCompletedFirstTabRotation] = useState(false)
  // Add states for fullscreen modal and video playing status
  let [isFullscreenOpen, setIsFullscreenOpen] = useState(false)
  let [isVideoPlaying, setIsVideoPlaying] = useState(false)
  // New state to track elements visibility for scroll animations
  let [visibleElements, setVisibleElements] = useState({
    title: false,
    description: false,
    categoryButtons: false,
    featureContent: false
  })
  
  // Add mobile detection
  let [isMobile, setIsMobile] = useState(false)
  
  let autoRotateTimerRef = useRef(null)
  let videoRef = useRef(null)
  let fullscreenVideoRef = useRef(null)
  let mobileVideoRef = useRef(null)
  let containerRef = useRef(null)
  let tabsRef = useRef([])
  
  // Refs for elements to animate on scroll
  let titleRef = useRef(null)
  let descriptionRef = useRef(null)
  let categoryButtonsRef = useRef(null)
  let featureContentRef = useRef(null)

  // Reference to the feature content for scrolling
  const featureDisplayRef = useRef(null);

  // Add a state to track which videos are actually loaded
  let [loadedVideos, setLoadedVideos] = useState({})

  // Add a state to track thumbnail sources
  let [thumbnailSources, setThumbnailSources] = useState({});

  // Add state to track if the fullscreen video is loaded
  let [isFullscreenVideoLoaded, setIsFullscreenVideoLoaded] = useState(false);

  // Reset tabsRef when features change
  useEffect(() => {
    tabsRef.current = tabsRef.current.slice(0, currentFeatures.length);
    setCompletedFirstTabRotation(false);
  }, [selectedTab]);
  
  // Setup intersection observer for scroll animations
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1 // When 10% of the element is visible
    };

    const handleIntersect = (entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Update state based on which element is visible
          const elementId = entry.target.dataset.id;
          if (elementId) {
            setVisibleElements(prev => ({
              ...prev,
              [elementId]: true
            }));
          }
          
          // Stop observing once it's been revealed
          observer.unobserve(entry.target);
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersect, options);
    
    // Observe all animated elements
    if (titleRef.current) observer.observe(titleRef.current);
    if (descriptionRef.current) observer.observe(descriptionRef.current);
    if (categoryButtonsRef.current) observer.observe(categoryButtonsRef.current);
    if (featureContentRef.current) observer.observe(featureContentRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  // Handle screen size changes and detect mobile
  useEffect(() => {
    let lgMediaQuery = window.matchMedia('(min-width: 1024px)')
    let mobileMediaQuery = window.matchMedia('(max-width: 768px)')

    function onMediaQueryChange({ matches }) {
      setTabOrientation(matches ? 'vertical' : 'horizontal')
    }
    
    function onMobileChange({ matches }) {
      setIsMobile(matches)
    }

    onMediaQueryChange(lgMediaQuery)
    onMobileChange(mobileMediaQuery)
    
    lgMediaQuery.addEventListener('change', onMediaQueryChange)
    mobileMediaQuery.addEventListener('change', onMobileChange)

    return () => {
      lgMediaQuery.removeEventListener('change', onMediaQueryChange)
      mobileMediaQuery.removeEventListener('change', onMobileChange)
    }
  }, [])

  // Reset the selected feature when changing tabs
  useEffect(() => {
    setSelectedFeatureIndex(0)
    setIsVideoLoaded(false)
    
    // Reset auto-rotation when changing tabs
    if (isAutoRotating) {
      resetAutoRotate()
    }
  }, [selectedTab])

  // Reset video loaded state when changing features
  useEffect(() => {
    setIsVideoLoaded(false)
    
    // Try to play the video when feature changes
    if (videoRef.current) {
      videoRef.current.load()
      
      // Add small delay to allow video to load before playing
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.play().catch(e => {
            console.log('Auto-play prevented:', e)
          })
        }
      }, 100)
    }
    
    // Also try to play the mobile video if it exists
    if (mobileVideoRef.current) {
      mobileVideoRef.current.load()
      
      setTimeout(() => {
        if (mobileVideoRef.current) {
          // Try to mute and play silently for autoplay
          mobileVideoRef.current.muted = true
          mobileVideoRef.current.play().catch(e => {
            console.log('Mobile auto-play prevented:', e)
          })
        }
      }, 100)
    }
  }, [selectedFeatureIndex])

  // Handle video play/pause events to control auto-rotation
  const handleVideoPlay = (e) => {
    // Stop auto-rotation for ANY video that plays, regardless of mute state
    setIsVideoPlaying(true)
    setIsAutoRotating(false)
    
    // Auto-open fullscreen when video is played on mobile and not already in fullscreen
    if (isMobile && !isFullscreenOpen && e.target === mobileVideoRef.current && !e.target.muted) {
      openFullscreen()
    }
  }

  const handleVideoPause = (e) => {
    // Only resume auto-rotation if we're not in fullscreen mode
    if (!isFullscreenOpen) {
      setIsVideoPlaying(false)
      setIsAutoRotating(true)
    }
    
    // If this is the mobile preview video, try to restart it silently
    if (e.target === mobileVideoRef.current && !isFullscreenOpen) {
      setTimeout(() => {
        if (mobileVideoRef.current) {
          mobileVideoRef.current.muted = true
          mobileVideoRef.current.play().catch(() => {})
        }
      }, 100)
    }
  }

  // Open fullscreen modal
  const openFullscreen = () => {
    setIsFullscreenOpen(true);
    // Reset fullscreen video loaded state
    setIsFullscreenVideoLoaded(false);
    // Pause auto-rotation when fullscreen is open
    setIsAutoRotating(false);
    
    // Make sure body doesn't scroll when modal is open
    document.body.style.overflow = 'hidden';
    
    // Pause the inline video if it's playing
    if (mobileVideoRef.current) {
      mobileVideoRef.current.pause();
    }
  };

  // Close fullscreen modal
  const closeFullscreen = () => {
    setIsFullscreenOpen(false)
    
    // Resume auto-rotation after closing fullscreen if video is not playing
    if (!isVideoPlaying) {
      setIsAutoRotating(true)
    }
    
    // Restore body scrolling
    document.body.style.overflow = 'auto'
    
    // Resume silent background playback for mobile preview
    if (mobileVideoRef.current) {
      mobileVideoRef.current.muted = true
      setTimeout(() => {
        mobileVideoRef.current.play().catch(() => {})
      }, 100)
    }
  }

  // Auto-rotate through features for mobile users
  useEffect(() => {
    // Only start auto-rotation if it's enabled and no video is currently playing
    if (isAutoRotating && !isVideoPlaying) {
      resetAutoRotate()
    } else if (!isAutoRotating && autoRotateTimerRef.current) {
      // Clear any existing timers if auto-rotation is disabled
      clearInterval(autoRotateTimerRef.current)
      autoRotateTimerRef.current = null
    }
    
    return () => {
      if (autoRotateTimerRef.current) {
        clearInterval(autoRotateTimerRef.current)
        autoRotateTimerRef.current = null
      }
    }
  }, [isAutoRotating, isVideoPlaying, selectedFeatureIndex, selectedTab])

  function resetAutoRotate() {
    if (autoRotateTimerRef.current) {
      clearInterval(autoRotateTimerRef.current)
    }
    
    // Rotate every 4 seconds instead of 6
    autoRotateTimerRef.current = setInterval(() => {
      const currentFeatures = selectedTab === 'sysadmin' ? features : auditFeatures
      
      if (selectedFeatureIndex === currentFeatures.length - 1) {
        // If we're on the last feature of the current tab
        if (selectedTab === 'sysadmin' && !completedFirstTabRotation) {
          // If we're on 'sysadmin' tab and haven't switched yet, prepare to switch
          setCompletedFirstTabRotation(true)
          // Wait a bit longer on the last item before switching tabs
          setTimeout(() => {
            setSelectedTab('audit')
            setSelectedFeatureIndex(0)
          }, 1000)
        } else {
          // Reset to first feature of current tab
          setSelectedFeatureIndex(0)
        }
      } else {
        // Move to next feature
        setSelectedFeatureIndex(prevIndex => (prevIndex + 1) % currentFeatures.length)
      }
    }, 4000)
  }

  function handleManualSelect(featureIndex) {
    setSelectedFeatureIndex(featureIndex)
    
    // Temporarily pause auto-rotation for 12 seconds when user manually selects
    setIsAutoRotating(false)
    
    // Resume auto-rotation after delay
    setTimeout(() => {
      setIsAutoRotating(true)
    }, 12000)
  }

  // Get current features based on selected tab
  const currentFeatures = selectedTab === 'sysadmin' ? features : auditFeatures
  const currentFeature = currentFeatures[selectedFeatureIndex]

  // Function to scroll to feature content when tab changes
  const scrollToFeatures = () => {
    if (featureDisplayRef.current) {
      // Get the position of the element relative to the viewport
      const rect = featureDisplayRef.current.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      // Calculate the absolute position and adjust for better positioning
      const absoluteTop = rect.top + scrollTop;
      const scrollPosition = absoluteTop - 20; // 20px offset for better viewing
      
      // Smooth scroll to the position
      window.scrollTo({
        top: scrollPosition,
        behavior: 'smooth'
      });
    }
  };
  
  // Handle tab changes
  const handleTabChange = (tab) => {
    setSelectedTab(tab);
    setSelectedFeatureIndex(0);
    
    // Small delay before scrolling to ensure DOM updates
    setTimeout(scrollToFeatures, 100);
  };

  // Touch event handlers for swipe functionality
  const handleTouchStart = (e) => {
    setTouchStartX(e.touches[0].clientX)
    setSwiping(true)
  }

  const handleTouchMove = (e) => {
    // Touch move handling kept minimal to ensure smooth scrolling
    if (!touchStartX || !swiping) return
  }

  const handleTouchEnd = (e) => {
    if (!touchStartX || !swiping) return
    
    const touchEndX = e.changedTouches[0].clientX
    const diffX = touchStartX - touchEndX
    
    // If the swipe is significant enough (more than 50px)
    if (Math.abs(diffX) > 50) {
      if (diffX > 0) {
        // Swipe left - go to next feature
        const nextIndex = (selectedFeatureIndex + 1) % currentFeatures.length
        handleManualSelect(nextIndex)
      } else {
        // Swipe right - go to previous feature
        const prevIndex = selectedFeatureIndex === 0 ? currentFeatures.length - 1 : selectedFeatureIndex - 1
        handleManualSelect(prevIndex)
      }
    }
    
    setTouchStartX(null)
    setSwiping(false)
  }

  const handleTouchCancel = () => {
    setTouchStartX(null)
    setSwiping(false)
  }
  
  // Helper functions for swipe navigation
  const goToNextFeature = () => {
    const nextIndex = (selectedFeatureIndex + 1) % currentFeatures.length
    handleManualSelect(nextIndex)
  }
  
  const goToPrevFeature = () => {
    const prevIndex = selectedFeatureIndex === 0 ? currentFeatures.length - 1 : selectedFeatureIndex - 1
    handleManualSelect(prevIndex)
  }

  // Add a function to dynamically generate thumbnails if they don't exist
  const ensureThumbnailExists = async (featureTitle, videoSrc) => {
    const thumbnailPath = getVideoThumbnail(featureTitle);
    
    // Create a new image and check if the thumbnail loads
    const img = new Image();
    
    return new Promise((resolve) => {
      img.onload = () => {
        // Thumbnail exists, use it
        resolve(thumbnailPath);
      };
      
      img.onerror = async () => {
        // Thumbnail doesn't exist, generate it on the fly
        try {
          // Generate a thumbnail from the video
          const dataUrl = await generateThumbnailFromVideo(videoSrc);
          
          // Use the data URL directly
          resolve(dataUrl);
        } catch (error) {
          console.error('Error generating thumbnail:', error);
          // Fallback to default thumbnail
          resolve('/thumbnails/default-thumbnail.jpg');
        }
      };
      
      // Start loading the image to check if it exists
      img.src = thumbnailPath;
    });
  };

  // Update the video play event to load the video on demand
  const handlePlayClick = (featureIndex) => {
    // Mark this video as loaded
    setLoadedVideos(prev => ({
      ...prev,
      [featureIndex]: true
    }))
    
    // Small delay to ensure the video element is updated
    setTimeout(() => {
      if (videoRef.current) {
        videoRef.current.play().catch(e => {
          console.log('Play prevented:', e)
        })
      }
    }, 100)
  }

  // Reset loaded videos when changing tabs
  useEffect(() => {
    setLoadedVideos({})
  }, [selectedTab])

  // Load thumbnails when feature or tab changes
  useEffect(() => {
    const loadThumbnail = async () => {
      if (!currentFeature) return;
      
      try {
        // Get thumbnail path or generate a thumbnail
        const thumbnailSrc = await ensureThumbnailExists(
          currentFeature.title, 
          currentFeature.video
        );
        
        // Update the thumbnail source
        setThumbnailSources(prev => ({
          ...prev,
          [selectedFeatureIndex]: thumbnailSrc
        }));
      } catch (error) {
        console.error('Error loading thumbnail:', error);
        // Use default thumbnail on error
        setThumbnailSources(prev => ({
          ...prev,
          [selectedFeatureIndex]: '/thumbnails/default-thumbnail.jpg'
        }));
      }
    };
    
    // Load the thumbnail
    loadThumbnail();
  }, [selectedFeatureIndex, selectedTab, currentFeature]);

  return (
    <section
      id="features"
      aria-label="Features"
      className="relative overflow-hidden bg-blue-800 pb-28 pt-20 sm:py-32"
    >
      {/* Gradient background overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-blue-900 via-blue-800 to-blue-700 opacity-90"></div>
      
      <Container className="relative">
        <div className="max-w-3xl md:mx-auto md:text-center xl:max-w-none">
          <div 
            data-id="title" 
            ref={titleRef} 
            className={`inline-flex items-center justify-center gap-2 mb-3 mx-auto text-center w-full transition-all duration-1000 transform ${
              visibleElements.title ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-8'
            }`}
          >
            <div className="h-px w-6 bg-blue-300"></div>
            <p className="text-blue-200 font-medium text-sm uppercase tracking-widest text-center">Enterprise Technology</p>
            <div className="h-px w-6 bg-blue-300"></div>
          </div>
          <h2 
            className={`font-display text-3xl font-bold tracking-tight text-white sm:text-4xl md:text-5xl text-center transition-all duration-1000 delay-300 transform ${
              visibleElements.title ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-8'
            }`}
          >
            Complete Audit & Compliance <span className="text-blue-200">Automation</span>
          </h2>
          <p 
            data-id="description" 
            ref={descriptionRef} 
            className={`mt-6 text-lg md:text-xl tracking-tight text-blue-100 max-w-2xl mx-auto md:leading-relaxed bg-blue-800/40 backdrop-blur-sm py-2 px-4 rounded-lg inline-block text-center transition-all duration-1000 delay-500 transform ${
              visibleElements.description ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            Purpose-built modules for both System Administrators and Internal Audit teams, creating a seamless integration between IT operations and compliance requirements.
          </p>
        </div>
        
        {/* Category Selector - Enhanced premium style */}
        <div 
          data-id="categoryButtons" 
          ref={categoryButtonsRef} 
          className={`mt-12 flex justify-center transition-all duration-1000 delay-700 transform ${
            visibleElements.categoryButtons ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
        >
          <div className="flex flex-row gap-4 w-full max-w-lg mx-auto">
            <button 
              onClick={() => handleTabChange('sysadmin')}
              className={`flex-1 group relative overflow-hidden rounded-xl transition-all duration-500 ${
                selectedTab === 'sysadmin' 
                  ? 'ring ring-white/40 shadow-xl transform scale-105' 
                  : 'opacity-90 hover:opacity-100 hover:shadow-lg hover:scale-102'
              }`}
            >
              {/* Premium gradient background */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 opacity-90"></div>
              
              {/* Accent pattern */}
              <div className="absolute inset-0 opacity-10">
                <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
                  <defs>
                    <pattern id="grid-pattern" patternUnits="userSpaceOnUse" width="20" height="20">
                      <path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(255, 255, 255, 0.3)" strokeWidth="0.5"></path>
                    </pattern>
                  </defs>
                  <rect width="100%" height="100%" fill="url(#grid-pattern)" />
                </svg>
              </div>
              
              {/* Highlight effect */}
              <div className="absolute top-0 left-0 right-0 h-1/2 bg-gradient-to-b from-white/20 to-transparent"></div>
              
              <div className="relative p-3 sm:p-6 flex flex-col items-center">
                <div className={`p-2 sm:p-3 bg-white/20 backdrop-blur-sm rounded-full mb-2 sm:mb-3 transform transition-transform duration-500 ${selectedTab === 'sysadmin' ? 'scale-110' : 'group-hover:scale-110'}`}>
                  <div className="text-white w-6 h-6 sm:w-8 sm:h-8">
                    <AdminIcon />
                  </div>
                </div>
                <h3 className="text-sm sm:text-lg font-bold text-white mb-0 sm:mb-1">System Admin</h3>
                <p className="text-blue-100 text-xs sm:text-sm text-center hidden sm:block">User access & compliance tools</p>
                
                {/* Fixed selection indicator */}
                {selectedTab === 'sysadmin' && (
                  <div className="absolute -bottom-1 left-0 w-full h-1 bg-white animate-pulse"></div>
                )}
                
                {/* Animated arrow indicator */}
                <div className={`absolute right-2 bottom-2 sm:right-3 sm:bottom-3 transition-all duration-500 ${
                  selectedTab === 'sysadmin' ? 'opacity-100' : 'opacity-0 translate-x-2 group-hover:opacity-70 group-hover:translate-x-0'
                }`}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white sm:w-4 sm:h-4">
                    <path d="M5 12h14" />
                    <path d="m12 5 7 7-7 7" />
                  </svg>
                </div>
              </div>
            </button>
            
            <button 
              onClick={() => handleTabChange('audit')}
              className={`flex-1 group relative overflow-hidden rounded-xl transition-all duration-500 ${
                selectedTab === 'audit' 
                  ? 'ring ring-white/40 shadow-xl transform scale-105' 
                  : 'opacity-90 hover:opacity-100 hover:shadow-lg hover:scale-102'
              }`}
            >
              {/* Premium gradient background */}
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 via-indigo-600 to-indigo-700 opacity-90"></div>
              
              {/* Accent pattern */}
              <div className="absolute inset-0 opacity-10">
                <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
                  <defs>
                    <pattern id="circle-pattern" patternUnits="userSpaceOnUse" width="20" height="20">
                      <circle cx="10" cy="10" r="1" fill="rgba(255, 255, 255, 0.4)" />
                    </pattern>
                  </defs>
                  <rect width="100%" height="100%" fill="url(#circle-pattern)" />
                </svg>
              </div>
              
              {/* Highlight effect */}
              <div className="absolute top-0 left-0 right-0 h-1/2 bg-gradient-to-b from-white/20 to-transparent"></div>
              
              <div className="relative p-3 sm:p-6 flex flex-col items-center">
                <div className={`p-2 sm:p-3 bg-white/20 backdrop-blur-sm rounded-full mb-2 sm:mb-3 transform transition-transform duration-500 ${selectedTab === 'audit' ? 'scale-110' : 'group-hover:scale-110'}`}>
                  <div className="text-white w-6 h-6 sm:w-8 sm:h-8">
                    <AuditIcon />
                  </div>
                </div>
                <h3 className="text-sm sm:text-lg font-bold text-white mb-0 sm:mb-1">Internal Audit</h3>
                <p className="text-indigo-100 text-xs sm:text-sm text-center hidden sm:block">Risk & control management</p>
                
                {/* Fixed selection indicator */}
                {selectedTab === 'audit' && (
                  <div className="absolute -bottom-1 left-0 w-full h-1 bg-white animate-pulse"></div>
                )}
                
                {/* Animated arrow indicator */}
                <div className={`absolute right-2 bottom-2 sm:right-3 sm:bottom-3 transition-all duration-500 ${
                  selectedTab === 'audit' ? 'opacity-100' : 'opacity-0 translate-x-2 group-hover:opacity-70 group-hover:translate-x-0'
                }`}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white sm:w-4 sm:h-4">
                    <path d="M5 12h14" />
                    <path d="m12 5 7 7-7 7" />
                  </svg>
                </div>
              </div>
            </button>
          </div>
        </div>
        
        <div 
          data-id="featureContent" 
          ref={featureContentRef} 
          className={`mt-16 grid grid-cols-1 items-start gap-y-2 sm:gap-y-6 md:mt-20 lg:grid-cols-12 lg:gap-x-8 transition-all duration-1000 delay-900 transform ${
            visibleElements.featureContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'
          }`}
        >
          {/* Feature Selection Area - RoastPlan-inspired */}
          <div className="lg:col-span-4 mb-10 lg:mb-0 lg:sticky lg:top-24">
            <div className="space-y-3 sm:space-y-5">
              {currentFeatures.map((feature, featureIndex) => (
                <button
                  key={feature.title}
                  onClick={() => handleManualSelect(featureIndex)}
                  className={`w-full text-left transition-all duration-300 group relative overflow-hidden rounded-2xl p-3 sm:p-4 pr-10 sm:pr-12 ${
                    selectedFeatureIndex === featureIndex
                      ? 'bg-gradient-to-r from-blue-500/20 to-indigo-500/20 ring-1 ring-white/20'
                      : 'hover:bg-white/5'
                  }`}
                >
                  {/* Selection marker */}
                  {selectedFeatureIndex === featureIndex && (
                    <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-blue-400 rounded-l"></div>
                  )}
                  
                  <div className="flex items-start">
                    <div className={`flex-shrink-0 p-1.5 sm:p-2 mr-2 sm:mr-3 rounded-lg ${
                      selectedFeatureIndex === featureIndex 
                        ? 'bg-blue-500/20 text-blue-300' 
                        : 'bg-blue-800/30 text-blue-200 group-hover:bg-blue-700/30'
                    }`}>
                      {feature.icon}
                    </div>
                    
                    <div className="flex-1 min-w-0 mr-8">
                      <h3 className={`text-sm sm:text-base md:text-lg font-semibold transition-colors break-words hyphens-auto leading-tight ${
                    selectedFeatureIndex === featureIndex
                          ? 'text-white' 
                          : 'text-blue-100 group-hover:text-white'
                      }`}>
                    {feature.title}
                  </h3>
                      <p className={`mt-1 text-xs sm:text-sm line-clamp-2 transition-colors ${
                      selectedFeatureIndex === featureIndex
                          ? 'text-blue-200' 
                          : 'text-blue-300 group-hover:text-blue-200'
                      }`}>
                    {feature.description}
                  </p>
                </div>
                    
                    {/* Arrow indicator */}
                    <div className={`flex-shrink-0 absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 transition-transform duration-300 ${
                      selectedFeatureIndex === featureIndex 
                        ? 'opacity-100' 
                        : 'opacity-0 -translate-x-2 group-hover:opacity-70 group-hover:translate-x-0'
                    }`}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-300 sm:w-5 sm:h-5">
                        <path d="M5 12h14" />
                        <path d="m12 5 7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Feature Content Display */}
          <div 
            className="lg:col-span-8"
            ref={featureDisplayRef}
          >
            <div
              ref={containerRef}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onTouchCancel={handleTouchCancel}
              className="relative"
            >
              <div className="overflow-hidden rounded-2xl bg-gradient-to-b from-blue-900/60 to-blue-900/90 backdrop-blur-sm shadow-2xl border border-white/10 transition-all duration-500">
                <div className="p-6 md:p-8">
                  <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-white mb-3 flex items-start break-words hyphens-auto leading-tight">
                    <span className="bg-blue-600/50 p-1.5 rounded-lg mr-2 sm:mr-3 text-blue-100 flex-shrink-0 mt-0.5">
                      {currentFeature.icon}
                    </span>
                    <span className="flex-1 min-w-0">{currentFeature.title}</span>
                  </h2>
                  
                  <p className="text-blue-200 text-sm md:text-lg mb-4 sm:mb-6 leading-relaxed break-words">
                    {currentFeature.description}
                  </p>
                  
                  <div className="relative rounded-xl overflow-hidden bg-black/30 border border-white/5 shadow-inner">
                    {/* Swipe indicators for mobile */}
                    <div 
                      className="absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-black/50 to-transparent flex items-center justify-center z-20 lg:hidden"
                      onClick={goToPrevFeature}
                    >
                      <div className="bg-white/10 hover:bg-white/20 rounded-full p-2 backdrop-blur-sm transition-all">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M15 18l-6-6 6-6"/>
                        </svg>
                      </div>
                    </div>
                    <div 
                      className="absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-black/50 to-transparent flex items-center justify-center z-20 lg:hidden"
                      onClick={goToNextFeature}
                    >
                      <div className="bg-white/10 hover:bg-white/20 rounded-full p-2 backdrop-blur-sm transition-all">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M9 18l6-6-6-6"/>
                        </svg>
                      </div>
                    </div>
                    
                    {/* Desktop Video */}
                    {!isMobile && (
                      <div className="relative">
                        {/* Show thumbnail until video is loaded */}
                        {!loadedVideos[selectedFeatureIndex] ? (
                          <div 
                            className="relative cursor-pointer"
                            onClick={() => handlePlayClick(selectedFeatureIndex)}
                          >
                            {/* Thumbnail image */}
                            <img
                              src={thumbnailSources[selectedFeatureIndex] || '/thumbnails/default-thumbnail.jpg'}
                              alt={`${currentFeature.title} preview`}
                              className="w-full h-auto object-contain max-h-[450px] rounded"
                            />
                            
                            {/* Play button overlay */}
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="rounded-full bg-blue-600/80 p-4 shadow-lg backdrop-blur-sm hover:bg-blue-600 transition-all transform hover:scale-110">
                                <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polygon points="5 3 19 12 5 21 5 3"></polygon>
                                </svg>
                              </div>
                            </div>
                            
                            {/* Click to play text */}
                            <div className="absolute bottom-4 left-0 right-0 text-center">
                              <span className="bg-black/60 text-white px-4 py-2 rounded-full text-sm backdrop-blur-sm">
                                Click to play video
                              </span>
                            </div>
                          </div>
                        ) : (
                          /* Only load the actual video when the thumbnail is clicked */
                          <video
                            ref={videoRef}
                            className={`w-full h-auto object-contain max-h-[450px] transition-all duration-700 ${isVideoLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
                            src={currentFeature.video}
                            playsInline
                            loop
                            controls
                            onLoadedData={() => setIsVideoLoaded(true)}
                            onPlay={handleVideoPlay}
                            onPause={handleVideoPause}
                          >
                            Your browser does not support the video tag.
                          </video>
                        )}
                      </div>
                    )}

                    {/* Mobile Video Player - Optimized for full width */}
                    {isMobile && (
                      <div className="relative -mx-6 md:mx-0">
                        {!loadedVideos[selectedFeatureIndex] ? (
                          <div 
                            className="relative cursor-pointer"
                            onClick={() => handlePlayClick(selectedFeatureIndex)}
                          >
                            {/* Mobile thumbnail */}
                            <img
                              src={thumbnailSources[selectedFeatureIndex] || '/thumbnails/default-thumbnail.jpg'}
                              alt={`${currentFeature.title} preview`}
                              className="w-full h-auto aspect-video object-cover rounded"
                            />
                            
                            {/* Play button overlay */}
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="rounded-full bg-blue-600/80 p-3 shadow-lg backdrop-blur-sm hover:bg-blue-600 transition-all transform hover:scale-110">
                                <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polygon points="5 3 19 12 5 21 5 3"></polygon>
                                </svg>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <video
                            ref={mobileVideoRef}
                            className={`w-full h-auto aspect-video object-cover transition-all duration-700 shadow-lg ${isVideoLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
                            src={currentFeature.video}
                            playsInline
                            loop
                            controls
                            controlsList="nodownload nofullscreen"
                            onLoadedData={() => setIsVideoLoaded(true)}
                            onPlay={handleVideoPlay}
                            onPause={handleVideoPause}
                            onVolumeChange={(e) => {
                              // User unmuted video - stop auto-rotation
                              if (!e.target.muted) {
                                setIsVideoPlaying(true)
                                setIsAutoRotating(false)
                              }
                            }}
                            onClick={(e) => {
                              // When clicked (not the controls), open fullscreen
                              if (e.target === mobileVideoRef.current) {
                                openFullscreen()
                              }
                            }}
                          >
                            Your browser does not support the video tag.
                          </video>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Mobile pagination indicators - redesigned */}
                <div className="flex justify-center py-4 bg-blue-950/60 lg:hidden">
                  <div className="flex space-x-3">
                    {currentFeatures.map((feature, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleManualSelect(idx)}
                        className={`rounded-full transition-all duration-300 ${
                          selectedFeatureIndex === idx 
                            ? 'w-8 h-3 bg-blue-300' 
                            : 'w-3 h-3 bg-blue-800 hover:bg-blue-700'
                        }`}
                        aria-label={`Go to feature ${idx + 1}`}
                      />
                    ))}
                    </div>
                  </div>
                </div>
            </div>
          </div>
        </div>
      </Container>

      {/* Fullscreen Video Modal for Mobile */}
      {isFullscreenOpen && (
        <div className="fixed inset-0 z-50 bg-black flex flex-col">
          {/* Header with title and close button */}
          <div className="flex justify-between items-center p-3 sm:p-4 bg-blue-900/80 shadow-md">
            <h3 className="text-white font-medium text-sm sm:text-base break-words hyphens-auto leading-tight pr-2 max-w-[80%]">
              {currentFeature.title}
            </h3>
            <button 
              onClick={closeFullscreen}
              className="text-white p-1.5 sm:p-2 rounded-full hover:bg-blue-800/50 active:bg-blue-800 flex-shrink-0"
              aria-label="Close fullscreen"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 sm:w-6 sm:h-6">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Fullscreen video container - optimized for device orientation */}
          <div className="flex-1 flex items-center justify-center bg-black p-0">
            {!isFullscreenVideoLoaded ? (
              <div className="relative w-full">
                {/* Thumbnail image */}
                <img
                  src={thumbnailSources[selectedFeatureIndex] || '/thumbnails/default-thumbnail.jpg'}
                  alt={`${currentFeature.title} preview`}
                  className="w-full h-auto max-h-[calc(100vh-140px)] object-contain"
                />
                
                {/* Play button overlay */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <button
                    onClick={() => setIsFullscreenVideoLoaded(true)}
                    className="rounded-full bg-blue-600/80 p-5 shadow-lg backdrop-blur-sm hover:bg-blue-600 transition-all transform hover:scale-110"
                    aria-label="Play video"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="5 3 19 12 5 21 5 3"></polygon>
                    </svg>
                  </button>
                </div>
                
                {/* Loading text */}
                <div className="absolute bottom-8 left-0 right-0 text-center">
                  <span className="bg-black/60 text-white px-6 py-3 rounded-full text-lg backdrop-blur-sm">
                    Tap to load video
                  </span>
                </div>
              </div>
            ) : (
              <video
                ref={fullscreenVideoRef}
                className="w-full h-auto max-h-screen object-contain"
                src={currentFeature.video}
                playsInline
                controls
                autoPlay
                controlsList="nodownload"
                onLoadedData={(e) => {
                  // Ensure the video is not muted in fullscreen mode
                  e.target.muted = false;
                  e.target.volume = 1.0;
                }}
                onPlay={handleVideoPlay}
                onPause={handleVideoPause}
                style={{ maxHeight: 'calc(100vh - 140px)' }} // Adjusted for header and info footer
              >
                Your browser does not support the video tag.
              </video>
            )}
          </div>
          
          {/* Description section at the bottom */}
          <div className="bg-black/80 backdrop-blur-sm border-t border-blue-900/50 py-2 px-4">
            <details className="text-blue-100">
              <summary className="font-medium text-sm cursor-pointer py-1 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
                </svg>
                Feature Description
              </summary>
              <p className="text-xs sm:text-sm py-2 leading-relaxed text-blue-200">
                {currentFeature.description}
              </p>
            </details>
          </div>
          
          {/* Feature navigation in fullscreen mode */}
          <div className="bg-blue-900/80 py-2 px-4 flex justify-between items-center">
            <button 
              onClick={() => {
                goToPrevFeature();
                // Small delay to load the new video
                setTimeout(() => {
                  if (fullscreenVideoRef.current) {
                    fullscreenVideoRef.current.load();
                    fullscreenVideoRef.current.play().catch(e => console.log('Play prevented:', e));
                  }
                }, 100);
              }}
              className="flex items-center gap-1 text-white/80 hover:text-white py-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
              </svg>
              <span className="text-sm">Previous</span>
            </button>
            
            <div className="flex gap-1">
              {currentFeatures.map((_, idx) => (
                <div 
                  key={idx}
                  className={`rounded-full transition-all ${
                    selectedFeatureIndex === idx 
                      ? 'w-4 h-2 bg-blue-300' 
                      : 'w-2 h-2 bg-blue-700/70'
                  }`}
                />
              ))}
            </div>
            
            <button 
              onClick={() => {
                goToNextFeature();
                // Small delay to load the new video
                setTimeout(() => {
                  if (fullscreenVideoRef.current) {
                    fullscreenVideoRef.current.load();
                    fullscreenVideoRef.current.play().catch(e => console.log('Play prevented:', e));
                  }
                }, 100);
              }}
              className="flex items-center gap-1 text-white/80 hover:text-white py-2"
            >
              <span className="text-sm">Next</span>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </section>
  )
}
